import type { ErrorBody, ValidationIssue } from '@verifyloop/core';

/**
 * Every call goes through here.
 *
 * Three things this wrapper is responsible for, all of them things a component
 * should never have to think about:
 *
 *  1. the bearer header, read from `localStorage` on each call rather than
 *     captured once, so signing out cannot leave a stale key in a closure;
 *  2. turning the server's `{ error: { code, message, details, retryable } }`
 *     envelope into a real `Error` carrying that message verbatim: the server
 *     writes those messages to be read by a human, so the UI shows them as-is;
 *  3. recognising the `validation` shape, where `details` is a
 *     `ValidationIssue[]` keyed by JSON path, and lifting it out so the plan
 *     editor can pin each issue to the field it names.
 */

export const API_KEY_STORAGE = 'verifyloop.apiKey';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly retryable: boolean;
  /** Non-empty only when `code === 'validation'` and the server sent issues. */
  readonly issues: readonly ValidationIssue[];
  readonly exitCode: string | null;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    details?: unknown;
    retryable?: boolean;
    exitCode?: string | null;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.details = init.details;
    this.retryable = init.retryable ?? false;
    this.issues = readIssues(init.code, init.details);
    this.exitCode = init.exitCode ?? null;
  }

  /** True for a key that was rejected or lacks a scope: the UI signs out. */
  get isAuthFailure(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

function readIssues(code: string, details: unknown): readonly ValidationIssue[] {
  if (code !== 'validation') return [];
  if (!Array.isArray(details)) return [];
  const issues: ValidationIssue[] = [];
  for (const entry of details) {
    if (
      typeof entry === 'object' &&
      entry !== null &&
      typeof (entry as ValidationIssue).path === 'string' &&
      typeof (entry as ValidationIssue).message === 'string'
    ) {
      const issue = entry as ValidationIssue;
      issues.push({
        path: issue.path,
        message: issue.message,
        severity: issue.severity === 'warning' ? 'warning' : 'error',
      });
    }
  }
  return issues;
}

/** A transport failure that never reached the server. */
export class NetworkError extends Error {
  constructor(message: string, override readonly cause?: unknown) {
    super(message);
    this.name = 'NetworkError';
  }
}

// ------------------------------------------------------------------ storage

export function readApiKey(): string | null {
  try {
    return window.localStorage.getItem(API_KEY_STORAGE);
  } catch {
    return null;
  }
}

export function writeApiKey(key: string | null): void {
  try {
    if (key === null) window.localStorage.removeItem(API_KEY_STORAGE);
    else window.localStorage.setItem(API_KEY_STORAGE, key);
  } catch {
    /* private mode: the session still works, it just will not survive a reload */
  }
}

// ------------------------------------------------------------------ request

export type QueryValue = string | number | boolean | undefined | null;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, QueryValue>;
  /** Set false for `/v1/health` and the auth pair. Defaults to true. */
  auth?: boolean;
  signal?: AbortSignal;
}

export function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const search = params.toString();
  return search ? `${path}?${search}` : path;
}

async function toApiError(response: Response): Promise<ApiError> {
  const exitCode = response.headers.get('x-verifyloop-exit-code');
  let payload: ErrorBody | null = null;
  try {
    const text = await response.text();
    payload = text ? (JSON.parse(text) as ErrorBody) : null;
  } catch {
    payload = null;
  }
  const error = payload?.error;
  return new ApiError({
    status: response.status,
    code: error?.code ?? `http_${response.status}`,
    message: error?.message ?? `${response.status} ${response.statusText || 'request failed'}`,
    details: error?.details,
    retryable: error?.retryable,
    exitCode,
  });
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, auth = true, signal } = options;
  const headers = new Headers({ accept: 'application/json' });

  if (auth) {
    const key = readApiKey();
    if (!key) {
      throw new ApiError({
        status: 401,
        code: 'auth',
        message: 'not signed in: this browser has no API key',
      });
    }
    headers.set('authorization', `Bearer ${key}`);
  }
  if (body !== undefined) headers.set('content-type', 'application/json');

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new NetworkError(
      'could not reach the Justloop server: is it running, and is the dev proxy pointed at it?',
      error,
    );
  }

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

/** Plain text (generated Playwright source, the agent skill). */
export async function requestText(path: string, options: RequestOptions = {}): Promise<string> {
  const response = await rawFetch(path, options);
  return response.text();
}

/** Binary (PNG / webm / HTML / ZIP). Authorization is required for every one. */
export async function requestBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const response = await rawFetch(path, options);
  return response.blob();
}

async function rawFetch(path: string, options: RequestOptions): Promise<Response> {
  const { auth = true, signal } = options;
  const headers = new Headers();
  if (auth) {
    const key = readApiKey();
    if (!key) {
      throw new ApiError({ status: 401, code: 'auth', message: 'not signed in: no API key' });
    }
    headers.set('authorization', `Bearer ${key}`);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), { headers, signal, method: 'GET' });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new NetworkError('could not reach the Justloop server', error);
  }
  if (!response.ok) throw await toApiError(response);
  return response;
}

/**
 * Artifacts are behind the bearer header, so `<img src>` will not work: the
 * bytes have to come through fetch and be handed to the DOM as an object URL.
 */
export async function artifactObjectUrl(
  path: string,
  signal?: AbortSignal,
): Promise<string> {
  const blob = await requestBlob(path, signal ? { signal } : {});
  return URL.createObjectURL(blob);
}

/** Fetch a ZIP and hand it to the browser as a download. */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const blob = await requestBlob(path);
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Give the browser a tick to start the download before releasing the blob.
  window.setTimeout(() => URL.revokeObjectURL(href), 30_000);
}

export const api = {
  get: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}): Promise<T> =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  del: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'DELETE' }),
  text: requestText,
  blob: requestBlob,
};

/** A one-line summary suitable for a toast. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError || error instanceof NetworkError) return error.message;
  if (error instanceof Error) return error.message;
  return String(error);
}